import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { FaRegTrashCan } from 'react-icons/fa6';
import {
    UPDATE_HOSPITAL_USER_PATH,
    UPDATE_NURSE_PATH,
    UPDATE_USER_PATH,
} from '@constants/routes';
import {
    adminDeleteUser,
    fetchCountries,
    getUserInfo,
    getUserName,
    handleFetchLogo,
} from '@utils/utils';
import {
    APPROVED,
    BLOCKED,
    HOSPITAL,
    INDIVIDUAL,
    NURSE,
    REJECTED,
    WAITING,
    WAITING_FOR_APPROVAL,
} from '@constants/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setGoToDashboard,
    setHospitalDetail,
    setNurseDetail,
    setUserDetail,
    setUserDeviceList,
    setUserEmail,
    setUserMobileNumber,
} from '@store/reducers/userSlice';
import {
    setAddressOne,
    setAddressTwo,
    setAdminCreateHospital,
    setAdminCreateIndividual,
    setAdminCreateNurse,
    setCity,
    setCountry,
    setDeviceUniqueID,
    setEmail,
    setHospitalBranch,
    setHospitalContact,
    setHospitalEmail,
    setHospitalLogoName,
    setHospitalName,
    setHospitalUserTableID,
    setHospitalWebsite,
    setMobileNumber,
    setName,
    setOldLogoName,
    setPincode,
    setUserRole,
    setUserState,
    setUserTableID,
    setHospitalID,
    setCountryList,
    setOldDeviceUuid,
    setOldDeviceId,
    setOldEmail,
    setCognitoUserId,
    setShowUpdatePassword,
    setHospitalsTableID,
    setHospitalGST,
} from '@store/reducers/auth/signupSlice';
import {
    addDeletionReason,
    getTableDataUsingUserId,
    getUserAttributes,
    handleDeleteUser,
} from '@utils/authUtils';
import ModalPopUp from '@components/ModalPopUpComponent';
import TextAreaPopUp from '@components/TextAreaPopUpComponent';
import { TENANT_ID } from '@constants/cognitoAttributes';
import { getDeviceId } from '@utils/deviceUtils';
import {
    capitalizeFirstLetter,
    getDataUsingTenantId,
    getDataUsingUserId,
} from '@utils/sharedUtils';
import { AdminDeleteUserCommand } from '@aws-sdk/client-cognito-identity-provider';
import { client } from '@utils/awsClients';

import awsConfig from '../../amplifyconfiguration.json';
import DataLoader from '@components/DataLoader';

import Swal from 'sweetalert2';
import { useAuditLog } from '../../contexts/AuditLogContext';
const MySwal = Swal;

// interface UserData {
//     id: number;
//     name: string;
//     email: string;
// }

// interface UserTableProps {
//     data: UserData[];
// }

interface UserDeviceList {
    id: number;
    name: string;
    date: string;
}
interface UserTableProps {
    data: any[];
    header: any[];
    row: number;
    totalData: any[];
}

interface UserData {
    id: string;
    name: string;
    email: string;
    createdAt: string;
    deviceId: string;
    mobileNo: string;
    role: string;
    slug: string;
    tenantId: string;
}

interface UserInfo {
    hospital_logo?: { S: string };
    hospital_name?: { S: string };
    hospital_branch?: { S: string };
    hospital_website?: { S: string };
    gst_no?: { S: string };
    hospital_contact?: { S: string };
    hospital_email?: { S: string };
    address1?: { S: string };
    address2?: { S: string };
    city?: { S: string };
    state?: { S: string };
    country?: { S: string };
    pincode?: { S: string };
    status?: { S: string };
}

type UserInfoResponse = UserInfo[];

const UserTable = ({ data, header, row, totalData }: UserTableProps) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const tenant_id = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const [updatedData, setData] = useState(data);
    const [rowPerPage, setRowPerPage] = useState(row);
    const [allData, setAllData] = useState(totalData);
    const [loading, setLoading] = useState(false);
    const [showTextModal, setShowTextModal] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { addLog } = useAuditLog();

    useEffect(() => {
        setData(data);
        // Ensure this reflects the updated state correctly
        if (totalData) setAllData(totalData);
        if (row) setRowPerPage(row);
    }, [data, row, totalData]);

    if (updatedData.length === 0) {
        return (
            <div className="card rounded-2xl w-full mx-auto p-0 !mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top m-3">
                            <tr>
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-4 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Get the keys from the first object in the data array
    let keys = Object.keys(updatedData[0]).filter(
        (key) =>
            key !== 'id' &&
            key !== 'slug' &&
            key !== 'userEmailStatus' &&
            key !== 'address' &&
            key !== 'userId' &&
            key !== 'createdAt_time' &&
            key !== 'role' &&
            key !== 'tenantId'
    );

    const hasHospital = updatedData.some(
        (user) =>
            user.role && user.role.toLowerCase() === HOSPITAL.toLowerCase()
    );
    const hasNurse = updatedData.some(
        (user) => user.role && user.role.toLowerCase() === NURSE.toLowerCase()
    );

    // Conditionally filter out 'device_id' if 'hospital_admin_status' is present
    if (hasHospital) {
        keys = keys.filter(
            (key) =>
                key !== 'deviceId' &&
                key !== 'mobileNo' &&
                key !== 'email' &&
                key !== 'hosUuid' &&
                key !== 'createdAt' &&
                key !== 'hospital_branch' &&
                key !== 'hospital_website' &&
                key !== 'gst_no' &&
                key !== 'name' &&
                key !== 'approved'
        );
    } else if (hasNurse) {
        keys = keys.filter(
            (key) =>
                key !== 'role' &&
                key !== 'tenantId' &&
                key !== 'deviceId' &&
                key !== 'mobileNo' &&
                key !== 'createdAt' &&
                key !== 'createdBy' &&
                key !== 'nurseId'
        );
    }

    const getStatusClass = (status: string) => {
        // console.log('Status:', status);
        switch (status.toLowerCase().trim()) {
            case APPROVED.toLowerCase():
                return 'text-white bg-success inline-block rounded-2xl px-3 py-1';
            case WAITING.toLowerCase():
                return 'text-white bg-warning inline-block rounded-2xl px-3 py-1';
            case WAITING_FOR_APPROVAL.toLowerCase():
                return 'text-white bg-warning inline-block rounded-2xl px-3 py-1';
            case BLOCKED.toLowerCase():
                return 'text-white bg-secondary inline-block rounded-2xl px-3 py-1';
            case REJECTED.toLowerCase():
                return 'text-white bg-danger inline-block rounded-2xl px-3 py-1';
            default:
                return '';
        }
    };

    const handleRowClick = async (user: any) => {
        // Determine the path based on the user's role or any other attribute
        // const path = user.hospital_admin_status
        //     ? `/hospital/${user.id}`
        //     : `/individual-user/${user.id}`;
        // dispatch(setUserDeviceList(list));
        // if () {}
        if (user.role && user.role.toLowerCase() == HOSPITAL.toLowerCase()) {
            dispatch(setGoToDashboard(false));
            const hospitalRes = await getUserInfo(
                'hospital_info',
                user.tenantId
            );
            const userData = await getUserInfo('user_info', user.tenantId);

            // Extracting additional details from the response
            if (hospitalRes && userData) {
                const additionalDetails = hospitalRes[0];
                const details = userData[0];

                // console.log(
                //     'additionalDetails.hospital_logo?.S || ',
                //     additionalDetails.hospital_logo?.S || ''
                // );

                const logo = await handleFetchLogo(
                    additionalDetails.hospital_logo?.S || ''
                );

                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: user.tenantId,
                    device_id: user.deviceId,
                    hospital_id: details.hospital_id?.S
                        ? details.hospital_id?.S
                        : '--',
                    hospital_name: additionalDetails.hospital_name?.S
                        ? additionalDetails.hospital_name?.S
                        : '--',
                    hospital_branch: additionalDetails.hospital_branch?.S
                        ? additionalDetails.hospital_branch?.S
                        : '--',
                    hospital_website: additionalDetails.hospital_website?.S
                        ? additionalDetails.hospital_website?.S
                        : '--',
                    gst_no: additionalDetails.gst?.S
                        ? additionalDetails.gst?.S
                        : '--',
                    contact_no: additionalDetails.hospital_contact?.S
                        ? additionalDetails.hospital_contact?.S
                        : '--',
                    email_id: additionalDetails.hospital_email?.S
                        ? additionalDetails.hospital_email?.S
                        : '--',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                    admin_name: user.name,
                    mobile_no: user.mobileNo,
                    admin_email: user.email,
                    create_date: user.createdAt,
                    hospital_admin_status: user.status
                        ? capitalizeFirstLetter(user.status)
                        : '--',
                    logo: logo || null,
                    approvedOn: additionalDetails.approved_on?.S
                        ? new Date(additionalDetails.approved_on.S).toLocaleDateString('en-GB')
                        : '--',
                };
                dispatch(setHospitalDetail(mergedUserDetails));
                navigate(`/hospital-users/${details.slug?.S}`);
            }
        } else if (
            user.role &&
            user.role.toLowerCase() == INDIVIDUAL.toLowerCase()
        ) {
            const res = await getUserInfo('user_info', user.tenantId);
            // Extracting additional details from the response
            if (res) {
                const additionalDetails = res[0];
                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: user.tenantId,
                    name: user.name,
                    device_id: user.deviceId,
                    email_id: user.email,
                    contact_id: user.mobileNo,
                    create_date: user.createdAt,
                    status: additionalDetails?.status?.S
                        ? additionalDetails?.status?.S.toLowerCase() !=
                          'blocked'
                            ? user.userEmailStatus
                            : additionalDetails?.status?.S
                        : '--',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                };
                // console.log('mergedUserDetails', mergedUserDetails);
                dispatch(setUserDetail(mergedUserDetails));
                navigate(`/individual-users/${user.slug}`);
            }
        } else if (
            user.role &&
            user.role.toLowerCase() == NURSE.toLowerCase()
        ) {
            // const res = await getUserInfo('user_info', user.tenantId);
            const res = await getTableDataUsingUserId('user_info', user.id);
            const attributes = await getUserAttributes();

            // // Extracting additional details from the response
            if (res && typeof attributes !== 'string' && attributes !== null) {
                const additionalDetails = res[0];

                let approved_By = '--';
                if (additionalDetails.approved_by?.S) {
                    const assigned = await getDataUsingUserId(
                        'user_info',
                        additionalDetails.approved_by?.S
                            ? additionalDetails.approved_by?.S
                            : ''
                    );

                    if (
                        assigned &&
                        assigned.Items &&
                        assigned.Items.length > 0
                    ) {
                        const data = assigned.Items[0];
                        approved_By = data.name?.S ? data.name?.S : '';
                    }
                }

                const created = await getDataUsingUserId(
                    'user_info',
                    additionalDetails.created_by?.S
                        ? additionalDetails.created_by?.S
                        : ''
                );

                let created_By = '--';
                if (created && created.Items && created.Items.length > 0) {
                    const userdata = created.Items[0];
                    created_By = userdata.name?.S ? userdata.name?.S : '';
                }

                // status: additionalDetails?.status?.S
                // ? additionalDetails?.status?.S.toLowerCase() ==
                //   WAITING.toLowerCase()
                //     ? WAITING_FOR_APPROVAL
                //     : additionalDetails?.status?.S
                // : '',
                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: additionalDetails.tenant_id?.S
                        ? additionalDetails.tenant_id?.S
                        : '',
                    name: user.name,
                    status: user?.status ? user?.status : '--',
                    deviceId: user.deviceId,
                    hospitalId: additionalDetails.hospital_id?.S
                        ? additionalDetails.hospital_id?.S
                        : '--',
                    hospitalName: additionalDetails.hospital_name?.S
                        ? additionalDetails.hospital_name?.S
                        : '--',
                    email_id: user.email,
                    contact_id: user.mobileNumber,
                    approvedBy: approved_By,
                    createdBy: created_By,
                    createdAt: user.createdAt,
                    nurseId: additionalDetails.nurse_id?.S
                        ? additionalDetails.nurse_id?.S
                        : '',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                };
                dispatch(setNurseDetail(mergedUserDetails));
                // navigate(`/management/nurse/${user.slug}`);
                navigate(`/nurse-users/${user.slug}`);
            }
        }
    };

    const handleUpdateUser = async (user: any) => {
        handleFetchCountries();
        // const deviceUUid = await getDeviceId(user.deviceId);

        // if (deviceUUid != '') {
        //     if (deviceUUid) {
        //         dispatch(
        //             setOldDeviceUuid(
        //                 deviceUUid.uuid?.S ? deviceUUid.uuid?.S : ''
        //             )
        //         );
        //         dispatch(
        //             setOldDeviceId(
        //                 deviceUUid.device_id?.S ? deviceUUid.device_id?.S : ''
        //             )
        //         );
        //     }
        // }

        if (user.role && user.role.toLowerCase() == HOSPITAL.toLowerCase()) {
            // dispatch(setUserEmail('sambantham@mirrar.in'));
            // dispatch(setUserMobileNumber('8754539295'));
            // dispatch(setUserRole(HOSPITAL));
            // navigate(UPDATE_HOSPITAL_USER_PATH);
            dispatch(setUserRole(HOSPITAL));
            // const res = await getUserInfo('user_info', user.tenantId);
            const hospitalRes = await getDataUsingTenantId(
                'hospital_info',
                user.tenantId
            );
            // console.log('res', res);
            const res = await getDataUsingUserId('user_info', user.id);

            if (res && res.Items && res.Items.length > 0) {
                const additionalDetails = res.Items[0];
                dispatch(setUserTableID(additionalDetails.uuid?.S || ''));
                dispatch(setAdminCreateHospital(true));
                dispatch(setShowUpdatePassword(false));
                dispatch(setDeviceUniqueID(user.deviceId));
                dispatch(setEmail(user.email));
                dispatch(setOldEmail(user.email));
                dispatch(setMobileNumber(user.mobileNo));
                dispatch(setName(additionalDetails.name?.S || ''));
                dispatch(setAddressOne(additionalDetails.address1?.S || ''));
                dispatch(setAddressTwo(additionalDetails.address2?.S || ''));
                dispatch(setCity(additionalDetails.city?.S || ''));
                dispatch(setCognitoUserId(user.id));
                dispatch(setHospitalsTableID(user.hosUuid));
                dispatch(setUserState(additionalDetails.state?.S || ''));
                dispatch(
                    setCountry(
                        additionalDetails.country?.S
                            ? additionalDetails.country?.S
                            : ''
                    )
                );
                dispatch(setPincode(additionalDetails.pincode?.S || ''));
                if (
                    hospitalRes &&
                    hospitalRes.Items &&
                    hospitalRes.Items.length > 0
                ) {
                    const details = hospitalRes.Items[0];

                    // console.log('details.uuid?.S', details.uuid?.S || '');
                    dispatch(setHospitalUserTableID(details.uuid?.S || ''));
                    dispatch(
                        setHospitalBranch(details.hospital_branch?.S || '')
                    );
                    dispatch(
                        setHospitalContact(details.hospital_contact?.S || '')
                    );
                    dispatch(setOldLogoName(details.hospital_logo?.S || ''));
                    dispatch(
                        setHospitalLogoName(details.hospital_logo?.S || '')
                    );
                    dispatch(setHospitalEmail(details.hospital_email?.S || ''));
                    dispatch(setHospitalName(details.hospital_name?.S || ''));
                    dispatch(
                        setHospitalWebsite(details.hospital_website?.S || '')
                    );
                    dispatch(setHospitalGST(details.gst?.S || ''));
                }
                navigate(UPDATE_HOSPITAL_USER_PATH);
            }
        } else if (
            user.role &&
            user.role.toLowerCase() == INDIVIDUAL.toLowerCase()
        ) {
            dispatch(setUserRole(INDIVIDUAL));
            // const res = await getUserInfo('user_info', user.tenantId);
            const res = await getDataUsingTenantId('user_info', user.tenantId);

            if (res && res.Items && res.Items.length > 0) {
                const additionalDetails = res.Items[0];
                dispatch(setUserTableID(additionalDetails.uuid?.S || ''));
                dispatch(setAdminCreateIndividual(true));
                dispatch(setCognitoUserId(user.id));
                dispatch(setDeviceUniqueID(user.deviceId));
                dispatch(setEmail(user.email));
                dispatch(setShowUpdatePassword(false));
                dispatch(setOldEmail(user.email));
                dispatch(setMobileNumber(user.mobileNo));
                dispatch(setName(additionalDetails.name?.S || ''));
                dispatch(setAddressOne(additionalDetails.address1?.S || ''));
                dispatch(setAddressTwo(additionalDetails.address2?.S || ''));
                dispatch(setCity(additionalDetails.city?.S || ''));
                dispatch(setUserState(additionalDetails.state?.S || ''));
                dispatch(setCountry(additionalDetails.country?.S || ''));
                dispatch(setPincode(additionalDetails.pincode?.S || ''));
                navigate(UPDATE_USER_PATH);
            }
            // dispatch(setUserEmail('sambantham@mirrar.in'));
            // dispatch(setUserMobileNumber('8754539295'));
            // dispatch(setUserRole(INDIVIDUAL));
            // navigate(UPDATE_USER_PATH);
        } else if (
            user.role &&
            user.role.toLowerCase() == NURSE.toLowerCase()
        ) {
            dispatch(setUserRole(NURSE));
            const res = await getDataUsingUserId('user_info', user.id);

            if (res && res.Items && res.Items.length > 0) {
                const additionalDetails = res.Items[0];
                dispatch(setUserTableID(additionalDetails.uuid?.S || ''));
                dispatch(setAdminCreateNurse(true));
                dispatch(setDeviceUniqueID(user.deviceId));
                dispatch(setCognitoUserId(user.id));
                dispatch(setShowUpdatePassword(false));
                dispatch(setEmail(user.email));
                dispatch(setOldEmail(user.email));
                dispatch(setMobileNumber(user.mobileNo));
                dispatch(setName(additionalDetails.name?.S || ''));
                dispatch(setAddressOne(additionalDetails.address1?.S || ''));
                dispatch(
                    setHospitalName(additionalDetails.hospital_name?.S || '')
                );
                dispatch(setHospitalID(additionalDetails.hospital_id?.S || ''));
                dispatch(setAddressTwo(additionalDetails.address2?.S || ''));
                dispatch(setCity(additionalDetails.city?.S || ''));
                dispatch(setUserState(additionalDetails.state?.S || ''));
                dispatch(
                    setCountry(
                        additionalDetails.country?.S
                            ? additionalDetails.country?.S
                            : ''
                    )
                );
                dispatch(setPincode(additionalDetails.pincode?.S || ''));
                navigate(UPDATE_NURSE_PATH);
            }
        }
    };

    const handleDelete = async (reason: string) => {
        // adminDeleteUser(user.email);
        setShowDeleteModal(false);
        setShowTextModal(false);
        // const attributes = await getUserAttributes();

        // if (typeof attributes !== 'string' && attributes !== null) {
        const updatedBy = tenant_id;

        if (userData) {
            // console.log('data', userData);
            try {
                setLoading(true);
                await addDeletionReason(updatedBy, reason, userData.id);
                await handleDeleteUser(
                    userData.tenantId,
                    userData.id,
                    userData.role
                ).then(() => {
                    const updatedUserData = updatedData.filter(
                        (item) => item.id !== userData.id
                    );

                    setData(updatedUserData);
                });
                await adminDeleteUser(userData.email);

                addLog(
                    'User Deleted',
                    userData.id,
                    'User',
                    loggedInUserID,
                    { 'User': userData.email, 'Reason': reason },
                    window.location.hostname
                );

                MySwal.fire({
                    position: 'top-end', // Position the toast in the top-right corner
                    icon: 'success',
                    title: 'User deleted successfully',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    customClass: {
                        popup: 'text-green bg-light_green', // Add custom class to the popup
                    },
                });
            } catch (error: any) {
                console.log('delete user error', error);
            } finally {
                setLoading(false);
            }
        }
        // }
    };

    const adminDeleteUser = async (email: string) => {
        const params = {
            UserPoolId: awsConfig.aws_user_pools_id,
            Username: email,
        };

        try {
            const command = new AdminDeleteUserCommand(params);
            const response = await client.send(command);
        } catch (error: any) {
            throw error;
        }
    };

    const handleFetchCountries = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            // Handle error message
            console.error('Error fetching countries:', list.error);
        }
    };

    const deleteUser = (user: any) => {
        setUserData(user);
        setShowDeleteModal(true);
    };

    // Calculate height based on number of rows and rows per page
    const tableHeight =
        allData.length >= 10 && rowPerPage === 10
            ? '36rem'
            : allData.length >= 5 && rowPerPage === 5
              ? '20rem'
              : 'auto';
    return (
        <>
            {/* <div className="card rounded-2xl w-full mx-auto p-4 !mt-4"> */}
            <div
                className="card rounded-2xl w-full mx-auto p-0 !mt-4"
            >
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="m-3">
                            <tr>
                                {/* <th></th> */}
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-4 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                                {/* <th></th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {updatedData.map((user) => (
                                <tr key={user.id}>
                                    {/* <td></td> */}
                                    {keys.map((key) => {
                                        const value = user[key];
                                        const isStatusKey =
                                            key.toLowerCase().trim() ===
                                            'status';
                                        return (
                                            <td
                                                key={key}
                                                onClick={() =>
                                                    handleRowClick(user)
                                                }
                                                className="cursor-pointer"
                                            >
                                                <div
                                                    className={`text-base font-normal text-table_header 
                                                    ${
                                                        isStatusKey
                                                            ? getStatusClass(
                                                                  value
                                                              )
                                                            : ''
                                                    }
                                                    `}
                                                >
                                                    {value ?? 'N/A'}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        {user.role &&
                                        user.role.toLowerCase() !=
                                            HOSPITAL.toLowerCase() ? (
                                            <div className="flex space-x-5 items-center">
                                                <div
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        handleUpdateUser(user)
                                                    }
                                                >
                                                    <MdEdit className="text-black" />
                                                </div>

                                                <div
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        deleteUser(user)
                                                    }
                                                >
                                                    <FaRegTrashCan className="text-danger" />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="">
                                                <div
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        handleUpdateUser(user)
                                                    }
                                                >
                                                    <MdEdit className="text-black mx-auto" />
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    {/* <td></td> */}
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <ModalPopUp
                show={showDeleteModal}
                message={t('pop_up.user_delete_msg')}
                onConfirm={() => {
                    setShowTextModal(true);
                    setShowDeleteModal(false);
                }}
                onCancel={() => setShowDeleteModal(false)}
            />
            <TextAreaPopUp
                show={showTextModal}
                message={t('pop_up.delete_reason_msg')}
                onConfirm={handleDelete}
                onCancel={() => {
                    setShowTextModal(false);
                    setShowDeleteModal(false);
                }}
            />
            {loading && <DataLoader />}
        </>
    );
};

export default UserTable;
